import { measurementType, measurementTypeOptions } from 'Models/Enums';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { store } from 'Models/Store';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';

export type FerryTripAvailability = {
	vehicle: number;
	/**
	 * The vehicle figure before it is floored.
	 *
	 * <p>`vehicle` is rounded down because a booker cannot have four fifths of a car space, and that
	 * is the right answer for every screen that offers one. It is the wrong answer for a manager
	 * weighing up an overbooked sailing: floored, 1.74 car lengths past full reads as `-2`, which
	 * is a different number from the one on the vessel. Kept beside it rather than replacing it, so
	 * nothing that asks "how many more fit" accidentally gets a fraction.</p>
	 */
	vehicleExact: number;
	trailer: number;
	passenger: number;
	totalVehicle: number;
	totalPassengers: number;
	totalTrailers: number;
	measurementType: measurementType;
};

export function calculateTotalPassengers(wizardData: BookingWizardData) {
	if (wizardData.tabSelected === 'passenger' || wizardData.tabSelected === 'vehicle') {
		return passengerTypeStore.getTotalFromWizardData(wizardData);
	}
	return 0;
}

interface CalculateAvailabilityParameter {
	passenger: {
		remaining?: number;
		capacity: number;
	};
	vehicle: {
		remaining?: number;
		capacity: number;
	};
	trailer: {
		remaining?: number;
		capacity: number;
	};
	weight?: {
		remaining?: number;
		capacity?: number;
	};
}

export const calculateVehicleSpacesLimitedByWeight = (weightRemaining: number) => {
	const minimumWeight = store.measurements
		.filter(x => x.measurementType === 'WEIGHT')
		.reduce((prev, curr) => {
			if (curr.value < prev) {
				return curr.value;
			}
			return prev;
		}, 2500);

	return weightRemaining / minimumWeight;
};

export const calculateAvailability = (param: CalculateAvailabilityParameter): FerryTripAvailability => {
	const vehicleLengthRemaining = param.vehicle?.remaining ?? 0;
	const trailerSpaceRemaining = param.trailer?.remaining ?? 0;
	const totalPassengerSpaces = param.passenger.capacity;
	const totalTrailerSpaces = param.trailer.capacity;

	// If a ferry has a max weight capacity assigned to it, we need to determine whether there is more weight spaces
	// available or more length spaces available.
	if (param.weight?.capacity && param.weight?.capacity > 0) {
		const minimumWeightMeasurement = store.measurements
			.filter(x => x.measurementType === 'WEIGHT')
			.sort((w1, w2) => (w1.value > w2.value ? 1 : -1))[0];
		const weightSpacesRemaining = (param.weight?.remaining ?? 0) / (minimumWeightMeasurement?.value ?? 1);

		let vehicleSpacesRemaining = vehicleLengthRemaining;
		let totalVehicleSpaces = param.vehicle.capacity;
		let measurementTypeBeingUsedToCalculate = measurementTypeOptions.LENGTH;

		if (weightSpacesRemaining < vehicleLengthRemaining) {
			vehicleSpacesRemaining = weightSpacesRemaining;
			totalVehicleSpaces = param?.weight?.capacity ?? 0 / (minimumWeightMeasurement?.value ?? 1);
			measurementTypeBeingUsedToCalculate = measurementTypeOptions.WEIGHT;
		}

		return {
			vehicle: Math.floor(vehicleSpacesRemaining),
			vehicleExact: vehicleSpacesRemaining,
			passenger: Math.floor(param.passenger?.remaining ?? 0),
			trailer: Math.floor(Math.min(weightSpacesRemaining / 2, trailerSpaceRemaining)),
			totalVehicle: totalVehicleSpaces,
			totalPassengers: totalPassengerSpaces,
			totalTrailers: totalTrailerSpaces,
			measurementType: measurementTypeBeingUsedToCalculate as measurementType,
		};
	}

	// Else just return the length space capacity available
	return {
		vehicle: Math.max(Math.floor(vehicleLengthRemaining)),
		vehicleExact: vehicleLengthRemaining,
		passenger: Math.max(Math.floor(param.passenger.remaining ?? 0)),
		trailer: Math.max(Math.floor(trailerSpaceRemaining)),
		totalVehicle: param.vehicle.capacity,
		totalPassengers: totalPassengerSpaces,
		totalTrailers: totalTrailerSpaces,
		measurementType: measurementTypeOptions.LENGTH as measurementType,
	};
};

// This will determine the availabilty of a ferry trip using the TicketTabTrip Entity
export function calculateAvailabilityFromTicketTabTrip(ferryTrip: TicketsTabTrip): FerryTripAvailability {
	const vehicleLengthRemaining = ferryTrip.vehicleSpacesAvailable ?? 0;
	const trailerSpaceRemaining = ferryTrip.trailerSpotsAvailable ?? 0;
	const totalPassengerSpaces = ferryTrip.passengerSpacesTotal ?? 0;
	const totalTrailerSpaces = ferryTrip.trailerSpotsTotal;

	// If a ferry has a max weight capacity assigned to it, we need to determine whether there is more weight spaces
	// available or more length spaces available.
	if (ferryTrip.totalWeight !== 0 && ferryTrip.totalWeight != null) {
		const minimumWeightMeasurement = store.measurements
			.filter(x => x.measurementType === 'WEIGHT')
			.sort((w1, w2) => (w1.value > w2.value ? 1 : -1))[0];
		const weightSpacesRemaining = (ferryTrip.weightAvailable ?? 0) / (minimumWeightMeasurement?.value ?? 1);
		let vehicleSpacesRemaining = vehicleLengthRemaining;
		let totalVehicleSpaces = ferryTrip.vehicleSpacesTotal;
		let measurementTypeBeingUsedToCalculate: measurementType = 'LENGTH';

		// If previously overbooked, focus on the allowed vehicle space by ignoring weight
		const space = ferryTrip.vehicleSpacesAvailable + ferryTrip.vehicleSpacesUsed;
		if (space <= ferryTrip.vehicleSpacesTotal) {
			if (weightSpacesRemaining < vehicleLengthRemaining) {
				vehicleSpacesRemaining = weightSpacesRemaining;
				totalVehicleSpaces = ferryTrip.totalWeight / (minimumWeightMeasurement?.value ?? 1);
				measurementTypeBeingUsedToCalculate = 'WEIGHT';
			}

			return {
				vehicle: Math.floor(vehicleSpacesRemaining),
				vehicleExact: vehicleSpacesRemaining,
				passenger: Math.floor(ferryTrip.passengerSpacesAvailable ?? 0),
				trailer: Math.floor(Math.min(weightSpacesRemaining / 2, trailerSpaceRemaining)),
				totalVehicle: totalVehicleSpaces,
				totalPassengers: totalPassengerSpaces,
				totalTrailers: totalTrailerSpaces,
				measurementType: measurementTypeBeingUsedToCalculate,
			};
		}
	}

	// Else just return the length space capacity available
	return {
		vehicle: Math.floor(vehicleLengthRemaining),
		vehicleExact: vehicleLengthRemaining,
		passenger: Math.floor(ferryTrip.passengerSpacesAvailable ?? 0),
		trailer: Math.floor(trailerSpaceRemaining),
		totalVehicle: ferryTrip.vehicleSpacesTotal,
		totalPassengers: totalPassengerSpaces,
		totalTrailers: totalTrailerSpaces,
		measurementType: measurementTypeOptions.LENGTH as measurementType,
	};
}
